import {all, call, put, takeLatest} from "redux-saga/effects";
import {PayloadAction} from "@reduxjs/toolkit";
import {
  FileEntity,
  UserAccountApiCompanyRequest,
  UserAccountApiCompanyResponse,
  UserAccountApiInternationalFlightToggleRequest, UserAccountApiInternationalFlightToggleResponse,
  UserAccountApiInternationalFlightUpdateRequest, UserAccountApiInternationalFlightUpdateResponse,
} from "../../../proto/generated/api_entities_pb";
import {API} from "../../../API/API";
import {getErrorMessageByCode} from "../../../utils/constans/errorCodes/errorCodes";
import {
  editCompany,
  internationalFlightToggle,
  internationalFlightUpload,
  loadCertificates,
  loadCompanyAvatar,
  setEditCompanyResult,
  setInternationalFlightToggleResult,
  setInternationalFlightUploadResult,
  setLoadCertificatesResult,
  setLoadCompanyAvatarResult,
} from "../../reducers/companyReducer/companyReducer";
import {
  IEditCompanyPayload,
  IInternationalFlightTogglePayload,
  IInternationalFlightUploadPayload,
  ILoadCompanyAvatarPayload,
  ILoadCompanyCertificatesPayload,
} from "../../reducers/companyReducer/companyReducerTypes";
import {UploadFileResponseType} from "../../../utils/helpers/fileHelpers/fileTypes";
import {getFileInfoAfterUpload} from "../../../utils/helpers/fileHelpers/getFileInfoAfterUpload";
import {mapICompanyToCompanyEntity} from "../../reducers/companyReducer/companyMappers";


function* editCompanySaga({payload}: PayloadAction<IEditCompanyPayload>) {
  const company = mapICompanyToCompanyEntity(payload.company);

  payload.description && company.setDescription(payload.description);
  payload.email && company.setEmail(payload.email);
  payload.phone && company.setPhone(payload.phone);
  payload.address && company.setAddress(payload.address);
  payload.website && company.setWebsite(payload.website);

  const req = new UserAccountApiCompanyRequest()
    .setCompany(company)

  try {
    const res: UserAccountApiCompanyResponse
      = yield call(API.editCompany, req);

    if (res.getResultresponse()?.getIssuccessful()) {
      yield put(setEditCompanyResult({
        item: res.getCompany(),
      }));
    } else {
      yield put(setEditCompanyResult({
        errorMessage: getErrorMessageByCode(res.getResultresponse()?.getErrormessage()),
      }));
    }
  } catch {
  }
}

function* loadCertificatesSaga({payload}: PayloadAction<ILoadCompanyCertificatesPayload>) {
  try {
    // Step 1: Upload basic certificates first
    const mappedCompany = mapICompanyToCompanyEntity(payload.company);

    const registrationCertificateFile: UploadFileResponseType = yield call(getFileInfoAfterUpload, payload.registrationCertificate);
    mappedCompany.setRegistrationcertificate(new FileEntity()
      .setFileid(registrationCertificateFile.fileId)
      .setName(registrationCertificateFile.fileName)
      .setFilepath(registrationCertificateFile.filePath)
    )

    if (payload.operatorCertificate) {
      const operatorCertificateFile: UploadFileResponseType = yield call(getFileInfoAfterUpload, payload.operatorCertificate);
      mappedCompany.setOperatorcard(new FileEntity()
        .setFileid(operatorCertificateFile.fileId)
        .setName(operatorCertificateFile.fileName)
        .setFilepath(operatorCertificateFile.filePath)
      )
    }

    const req = new UserAccountApiCompanyRequest()
      .setCompany(mappedCompany)

    const res: UserAccountApiCompanyResponse
      = yield call(API.editCompany, req);

    if (!res.getResultresponse()?.getIssuccessful()) {
      yield put(setLoadCertificatesResult({
        errorMessage: getErrorMessageByCode(res.getResultresponse()?.getErrormessage()),
      }));
      return;
    }

    // Step 2: If international flights are enabled and files are provided, upload them
    let userInternationalFlight;
    if (payload.internationalFlightsEnabled && (payload.caaOperatorApproval || payload.easaTcoAuthorisation || payload.faaAuthorization)) {
      const internationalReq = new UserAccountApiInternationalFlightUpdateRequest();

      // Upload CAA file if provided
      if (payload.caaOperatorApproval) {
        const caaFile: UploadFileResponseType = yield call(getFileInfoAfterUpload, payload.caaOperatorApproval);
        internationalReq.setCaaoperatorapprovalfileid(caaFile.fileId);
      }

      // Upload EASA file if provided
      if (payload.easaTcoAuthorisation) {
        const easaFile: UploadFileResponseType = yield call(getFileInfoAfterUpload, payload.easaTcoAuthorisation);
        internationalReq.setEasatcoauthorizationfileid(easaFile.fileId);
      }

      // Upload FAA file if provided
      if (payload.faaAuthorization) {
        const faaFile: UploadFileResponseType = yield call(getFileInfoAfterUpload, payload.faaAuthorization);
        internationalReq.setFaaauthorizationfileid(faaFile.fileId);
      }

      const internationalRes: UserAccountApiInternationalFlightUpdateResponse = yield call(API.internationalFlightUpdate, internationalReq);

      if (!internationalRes.getResultresponse()?.getIssuccessful()) {
        yield put(setLoadCertificatesResult({
          errorMessage: getErrorMessageByCode(internationalRes.getResultresponse()?.getErrormessage()),
        }));
        return;
      }
      
      userInternationalFlight = internationalRes.getUserinternationalflight();
    }

    // Both uploads successful, return the company from the first response and international flight data if available
    yield put(setLoadCertificatesResult({
      company: res.getCompany(),
      userInternationalFlight: userInternationalFlight || undefined,
    }));
  } catch {
  }
}


function* loadCompanyAvatarSaga({payload}: PayloadAction<ILoadCompanyAvatarPayload>) {
  try {
    const avatarFile: UploadFileResponseType = yield call(getFileInfoAfterUpload, payload.file);

    const mappedCompany = mapICompanyToCompanyEntity(payload.company);
    mappedCompany.setAvatar(new FileEntity()
      .setFileid(avatarFile.fileId)
      .setName(avatarFile.fileName)
      .setFilepath(avatarFile.filePath)
    )

    const req = new UserAccountApiCompanyRequest()
      .setCompany(mappedCompany)

    const res: UserAccountApiCompanyResponse
      = yield call(API.editCompany, req);

    if (res.getResultresponse()?.getIssuccessful()) {
      yield put(setLoadCompanyAvatarResult({
        company: res.getCompany(),
      }));
    } else {
      yield put(setLoadCompanyAvatarResult({
        errorMessage: getErrorMessageByCode(res.getResultresponse()?.getErrormessage()),
      }));
    }
  } catch {
  }
}

function* internationalFlightToggleSaga({payload}: PayloadAction<IInternationalFlightTogglePayload>) {
  try {
    const req = new UserAccountApiInternationalFlightToggleRequest()
      .setEnabled(payload.enabled);

    const res: UserAccountApiInternationalFlightToggleResponse = yield call(API.internationalFlightToggle, req);

    if (res.getResultresponse()?.getIssuccessful()) {
      // Determine if enabled based on whether UserInternationalFlightEntity exists in response
      const userInternationalFlight = res.getUserinternationalflight();
      const isEnabled = userInternationalFlight !== undefined;
      yield put(setInternationalFlightToggleResult({
        success: true,
        enabled: isEnabled,
        userInternationalFlight: userInternationalFlight || undefined,
      }));
    } else {
      yield put(setInternationalFlightToggleResult({
        errorMessage: getErrorMessageByCode(res.getResultresponse()?.getErrormessage()),
      }));
    }
  } catch {
  }
}

function* internationalFlightUploadSaga({payload}: PayloadAction<IInternationalFlightUploadPayload>) {
  try {
    const req = new UserAccountApiInternationalFlightUpdateRequest();

    // Upload CAA file if provided
    let caaFileId: string | undefined;
    if (payload.caaOperatorApproval) {
      const caaFile: UploadFileResponseType = yield call(getFileInfoAfterUpload, payload.caaOperatorApproval);
      caaFileId = caaFile.fileId;
      req.setCaaoperatorapprovalfileid(caaFileId);
    }

    // Upload EASA file if provided
    let easaFileId: string | undefined;
    if (payload.easaTcoAuthorisation) {
      const easaFile: UploadFileResponseType = yield call(getFileInfoAfterUpload, payload.easaTcoAuthorisation);
      easaFileId = easaFile.fileId;
      req.setEasatcoauthorizationfileid(easaFileId);
    }

    // Upload FAA file if provided
    let faaFileId: string | undefined;
    if (payload.faaAuthorization) {
      const faaFile: UploadFileResponseType = yield call(getFileInfoAfterUpload, payload.faaAuthorization);
      faaFileId = faaFile.fileId;
      req.setFaaauthorizationfileid(faaFileId);
    }

    // Only call update if at least one file was uploaded
    if (caaFileId || easaFileId || faaFileId) {
      const res: UserAccountApiInternationalFlightUpdateResponse = yield call(API.internationalFlightUpdate, req);

      if (res.getResultresponse()?.getIssuccessful()) {
        const userInternationalFlight = res.getUserinternationalflight();
        yield put(setInternationalFlightUploadResult({
          success: true,
          userInternationalFlight: userInternationalFlight || undefined,
        }));
      } else {
        yield put(setInternationalFlightUploadResult({
          errorMessage: getErrorMessageByCode(res.getResultresponse()?.getErrormessage()),
        }));
      }
    } else {
      yield put(setInternationalFlightUploadResult({success: true}));
    }
  } catch {
  }
}


export function* companySaga() {
  yield all([
    takeLatest(editCompany.type, editCompanySaga),
    takeLatest(loadCertificates.type, loadCertificatesSaga),
    takeLatest(loadCompanyAvatar.type, loadCompanyAvatarSaga),
    takeLatest(internationalFlightToggle.type, internationalFlightToggleSaga),
    takeLatest(internationalFlightUpload.type, internationalFlightUploadSaga),
  ])
}
